import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Flex, Tag, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { OAuthTokenOut, UserOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { unixToIso } from '../constants';

const { Text } = Typography;

const SecretText = ({ value }: { value?: string | null }) =>
  orDash(
    value ? (
      <Text code copyable ellipsis style={{ maxWidth: 320 }}>
        {value}
      </Text>
    ) : null
  );

interface OAuthTokenGeneralTabProps {
  record: OAuthTokenOut;
  user?: UserOut;
}

export const OAuthTokenGeneralTab = ({
  record,
  user,
}: OAuthTokenGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();
  const revoked = record.revoked ?? false;
  const seconds = (value: number) => `${value} ${t('common.seconds')}`;

  const infoItems: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'user',
      label: t('iam.oauth_tokens.fields.user'),
      children: (
        <ShowIconLink resource="iam.users" id={record.user_id}>
          {user?.email ?? user?.username ?? record.user_id}
        </ShowIconLink>
      ),
    },
    {
      key: 'client_id',
      label: t('iam.oauth_tokens.fields.client_id'),
      children: orDash(
        record.client_id ? (
          <Text code copyable>
            {record.client_id}
          </Text>
        ) : null
      ),
    },
    {
      key: 'token_type',
      label: t('iam.oauth_tokens.fields.token_type'),
      children: orDash(record.token_type ?? 'Bearer'),
    },
    {
      key: 'revoked',
      label: t('iam.oauth_tokens.fields.revoked'),
      children: (
        <Tag color={revoked ? 'error' : 'success'}>
          {t(`iam.oauth_tokens.status.${revoked ? 'revoked' : 'active'}`)}
        </Tag>
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  const detailItems: DescriptionsProps['items'] = [
    {
      key: 'access_token',
      label: t('iam.oauth_tokens.fields.access_token'),
      span: 3,
      children: <SecretText value={record.access_token} />,
    },
    {
      key: 'refresh_token',
      label: t('iam.oauth_tokens.fields.refresh_token'),
      span: 3,
      children: <SecretText value={record.refresh_token} />,
    },
    {
      key: 'scope',
      label: t('iam.oauth_tokens.fields.scope'),
      span: 3,
      children: orDash(record.scope),
    },
    {
      key: 'issued_at',
      label: t('iam.oauth_tokens.fields.issued_at'),
      children: orDash(formatDateTime(unixToIso(record.issued_at))),
    },
    {
      key: 'expires_in',
      label: t('iam.oauth_tokens.fields.expires_in'),
      children: seconds(record.expires_in),
    },
    {
      key: 'refresh_token_expires_in',
      label: t('iam.oauth_tokens.fields.refresh_token_expires_in'),
      children: seconds(record.refresh_token_expires_in),
    },
  ];

  const deviceItems: DescriptionsProps['items'] = [
    {
      key: 'device_id',
      label: t('iam.oauth_tokens.fields.device_id'),
      children: orDash(
        record.device_id ? <Text code>{record.device_id}</Text> : null
      ),
    },
    {
      key: 'device_name',
      label: t('iam.oauth_tokens.fields.device_name'),
      children: orDash(record.device_name),
    },
    {
      key: 'ip_address',
      label: t('iam.oauth_tokens.fields.ip_address'),
      children: orDash(record.ip_address),
    },
    {
      key: 'user_agent',
      label: t('iam.oauth_tokens.fields.user_agent'),
      span: 3,
      children: orDash(record.user_agent),
    },
  ];

  return (
    <Flex vertical gap={token.margin}>
      <Card title={t('iam.oauth_tokens.sections.token_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={infoItems}
        />
      </Card>
      <Card title={t('iam.oauth_tokens.sections.token_details')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={detailItems}
        />
      </Card>
      <Card title={t('iam.oauth_tokens.sections.device_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={deviceItems}
        />
      </Card>
    </Flex>
  );
};
